import { StreetAddress } from './StreetAddress'

/**
 * Compare two strings by their natural order, where numbers are compared numerically and non-numbers are compared alphabetically
 * @param stringA - The first string to compare
 * @param stringB - The second string to compare
 * @returns A number indicating which string should come first in the sorted order
 * * Less than 0: stringA should come before stringB
 * * 0: stringA and stringB are equal
 * * Greater than 0: stringA should come after stringB
 */
export const compareStringsForNaturalOrderSort = (stringA: string, stringB: string): number => {
  const partsA: string[] = []
  const partsB: string[] = []
  stringA.replace(/(\d+)|(\D+)/g, (_, digitGroup, nonDigitGroup) => {
    partsA.push(digitGroup || nonDigitGroup)
    return ''
  })
  stringB.replace(/(\d+)|(\D+)/g, (_, digitGroup, nonDigitGroup) => {
    partsB.push(digitGroup || nonDigitGroup)
    return ''
  })

  for (let i = 0; i < Math.min(partsA.length, partsB.length); i++) {
    const partA = partsA[i]
    const partB = partsB[i]
    if (partA === partB) {
      continue
    }
    const numA = parseInt(partA, 10)
    const numB = parseInt(partB, 10)
    // If both parts are numbers, compare them
    if (!isNaN(numA) && !isNaN(numB)) {
      return numA - numB
    }
    // If both parts are not numbers, compare them as strings
    return partA.localeCompare(partB, undefined, { sensitivity: 'base' })
  }

  return partsA.length - partsB.length
}

/**
 * Compare two strings using custom logic to produce an intuitive, human-friendly sort order
 * Handles various string types including street addresses, numbers, place names, and object names
 * If both strings are street addresses, use the StreetAddress.compare method to compare them, otherwise fallback to a natural order sort
 * @param stringA - The first string to compare
 * @param stringB - The second string to compare
 * @returns A number indicating which string should come first in the sorted order
 * * Less than 0: stringA should come before stringB
 * * 0: stringA and stringB are equal
 * * Greater than 0: stringA should come after stringB
 */
export const compareStringsForIntuitiveSort = (stringA: string, stringB: string): number => {
  if (StreetAddress.isStreetAddress(stringA) && StreetAddress.isStreetAddress(stringB)) {
    return StreetAddress.compare(stringA, stringB)
  }

  return compareStringsForNaturalOrderSort(stringA, stringB)
}

/**
 * Build a sorter function that orders objects based on the value of a key attribute using intuitive string sorting
 * @param key - The key attribute of the object that the sorter is based on
 * @returns A sorter function that can be applied to an array of objects to order them based on the value of the key attribute
 */
export const buildIntuitiveStringSorterBy = <T>(key: keyof T): ((a: T, b: T) => number) => {
  return (a, b) => compareStringsForIntuitiveSort(String(a[key]), String(b[key]))
}

/**
 * Score a string based on how well its words match the words in the search query
 * @param candidateString - The string to score
 * @param searchQueryWords - The words in the search query
 * @returns A number indicating the score of the string based on how well its words match the words in the search query
 * * The higher the score, the better the string matches the search query
 * * The score is the sum of the following points for each matching word:
 * * * 100 points if the first word of both the string and search query match
 * * * 50 points minus the index of the word match for each subsequent matching word
 */
const scoreStringForSearchQuery = (candidateString: string, searchQueryWords: string[]): number => {
  const candidateStringWords = candidateString.toLowerCase().split(/\s+/)
  let score = 0

  searchQueryWords.forEach((searchQueryWord, searchQueryWordIndex) => {
    const candidateStringWordIndex = candidateStringWords.findIndex((candidateStringWord) => candidateStringWord.startsWith(searchQueryWord))
    if (candidateStringWordIndex === -1) {
      return
    }
    // First word of both the candidate and search query match at the start
    if (searchQueryWordIndex === 0 && candidateStringWordIndex === 0) {
      score += 100
    }
    // Subsequent word matches
    score += Math.max(50 - candidateStringWordIndex, 0)
  })

  return score
}

/**
 * Build a sorter function that orders strings using custom logic to produce an intuitive, human-friendly sort order based on how well they match a search query
 * @param queryString - The search query string to use to help prioritise results
 * @returns A sorter function that orders strings using intuitive logic based on how well they match the search query string
 */
export const buildIntuitiveSearchResultSorter = (queryString: string) => {
  const queryStringLower = queryString.toLowerCase()
  const queryStringWords = queryStringLower.split(/\s+/)

  return (stringA: string, stringB: string) => {
    // Exact matches of the query string take the highest precedence, the closer to the start of the string the higher the precedence
    const aIndex = stringA.toLowerCase().indexOf(queryStringLower)
    const bIndex = stringB.toLowerCase().indexOf(queryStringLower)
    // Query string is in stringB, but not in stringA
    if (aIndex !== -1 && bIndex === -1) {
      return -1
    }
    // Query string is in stringA, but not in stringB
    if (bIndex !== -1 && aIndex === -1) {
      return 1
    }
    // Query string is in both stringA and stringB, but at different positions
    if (aIndex !== -1 && bIndex !== -1 && aIndex !== bIndex) {
      return aIndex - bIndex
    }

    // Word-based scoring, where matches are based on the start of words, and the closer to the start the better
    const aScore = scoreStringForSearchQuery(stringA, queryStringWords)
    const bScore = scoreStringForSearchQuery(stringB, queryStringWords)
    if (aScore !== bScore) {
      return bScore - aScore
    }

    // Fallback to more general string comparison
    return compareStringsForIntuitiveSort(stringA, stringB)
  }
}

/**
 * Build a sorter function that orders objects using intuitive logic based on how well the value of a key attribute matches a search query
 * @param key - The key attribute of the object that the sort is based on
 * @param queryString - The search query string to use to help prioritise results
 * @returns A sorter function that orders objects based on how well the value of the key attribute matches the search query string
 */
export const buildIntuitiveSearchResultSorterBy = <T>(key: keyof T, queryString: string): ((a: T, b: T) => number) => {
  const resultsSorter = buildIntuitiveSearchResultSorter(queryString)
  return (a, b) => resultsSorter(String(a[key]), String(b[key]))
}
